import { computed } from 'mobx'
import { task } from 'mobx-task'
import { switchMap } from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { type Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { isNotNil } from '@/utils/object-util'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { type Job } from '@/features/jobs/Job'
import type { RequestedSupportingItem } from '@/features/jobs/info-gathering/RequestedSupportingItem'
import type { DependentsInfoSupportingItemInput } from './DependentsInfoSupportingItemInput'
import {
  formatFullName,
  formatProfileDate,
  maskPersonalTaxpayerIdentificationNumber,
} from '@/features/jobs/info-gathering/info-gathering-formatters'
import { type IndividualProfileStore } from './IndividualProfileStore'

/**
 * The dependent profiles the client provided for a job.
 */
export class DependentProfileViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Loads the dependent profiles.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._fetch(signal)
  })

  constructor(
    private readonly _job: Job,
    readonly item: RequestedSupportingItem<DependentsInfoSupportingItemInput>,
    private readonly _individualProfileStore: IndividualProfileStore,
  ) {
    // Re-fetches the profiles when the provided dependents change.
    this._lifecycle.manage(
      toStream(() => this.item.input?.dependents).pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            // A failed refresh keeps showing the previously loaded value.
            this._fetch(signal).catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * The provided dependent profile versions that have been fetched.
   */
  @computed get dependentProfiles() {
    const dependents = this.item.input?.dependents ?? []
    return dependents
      .filter((d) => d.profileId && d.profileVersion)
      .map((d) =>
        this._individualProfileStore.getDependentProfile(
          d.profileId,
          d.profileVersion,
        ),
      )
      .filter(isNotNil)
  }

  /**
   * Whether the provided dependent profiles have been fetched; editing before
   * then would start without them and delist them.
   */
  @computed get isLoaded() {
    return (
      !this.item.completed ||
      this.dependentProfiles.length ===
        (this.item.input?.dependents ?? []).filter(
          (d) => d.profileId && d.profileVersion,
        ).length
    )
  }

  /**
   * The dependents, formatted for display.
   */
  @computed get dependents(): DependentProfileDisplay[] {
    return this.dependentProfiles.map((profile) => ({
      id: profile.id,
      title: `${formatFullName(profile.personalInfo)} (${profile.relationship})`,
      dateOfBirth: formatProfileDate(profile.personalInfo.dateOfBirth),
      personalTaxpayerIdentificationNumber:
        profile.personalInfo.personalTaxpayerIdentificationNumber,
      maskedPersonalTaxpayerIdentificationNumber:
        maskPersonalTaxpayerIdentificationNumber(
          profile.personalInfo.personalTaxpayerIdentificationNumber,
        ),
    }))
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The section renders a failure from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _fetch(signal: AbortSignal) {
    const input = this.item.input
    if (!this.item.completed || !input) {
      return
    }

    await Promise.all(
      input.dependents.map((d) =>
        this._individualProfileStore.fetchDependentProfileVersionForJob(
          d.profileId,
          this._job.id,
          signal,
        ),
      ),
    )
  }
}

/**
 * A dependent formatted for display.
 */
export interface DependentProfileDisplay {
  id: Id
  title: string
  dateOfBirth: string
  personalTaxpayerIdentificationNumber: string
  maskedPersonalTaxpayerIdentificationNumber: string
}
